/**
 * Monotone cubic муруй (Fritsch–Carlson) — орлогын график, sparkline.
 *
 * ⚠️ Энгийн Catmull-Rom нь өгөгдөл огцом өөрчлөгдөхөд шугамыг тэгээс
 *    ДООШ цухуйлгадаг — орлогын графикт сөрөг утга харагдана. Энд
 *    налууг хязгаарлаж, цэгүүдийн хооронд хэзээ ч хэтрэхгүй.
 *
 * `revenue-plot.tsx`-ээс гаргав: дашбоардын sparkline ба графикийн
 * «толгойн цэг» (`monotoneY`) ижил муруйг хуваалцана.
 */

export interface Pt {
  x: number;
  y: number;
}

/** Fritsch–Carlson налуу — тэгээс доош цухуйхгүй. */
function tangents(y: number[]): number[] {
  const n = y.length;
  const d = Array.from({ length: n - 1 }, (_, i) => (y[i + 1] ?? 0) - (y[i] ?? 0));
  const m = new Array<number>(n).fill(0);

  m[0] = d[0] ?? 0;
  m[n - 1] = d[n - 2] ?? 0;
  for (let i = 1; i < n - 1; i += 1) m[i] = ((d[i - 1] ?? 0) + (d[i] ?? 0)) / 2;

  for (let i = 0; i < n - 1; i += 1) {
    const di = d[i] ?? 0;
    if (di === 0) {
      m[i] = 0;
      m[i + 1] = 0;
      continue;
    }
    const a = (m[i] ?? 0) / di;
    const b = (m[i + 1] ?? 0) / di;
    const s = a * a + b * b;
    if (s > 9) {
      const t = (3 / Math.sqrt(s)) * di;
      m[i] = t * a;
      m[i + 1] = t * b;
    }
  }
  return m;
}

/** SVG `d` атрибут. Цэгүүд x-ээр ЖИГД зайтай гэж үзнэ. */
export function monotonePath(pts: Pt[]): string {
  if (pts.length === 0) return '';
  if (pts.length === 1) return `M${pts[0]?.x ?? 0} ${pts[0]?.y ?? 0}`;

  const m = tangents(pts.map((p) => p.y));
  let d = `M${pts[0]?.x.toFixed(2)} ${pts[0]?.y.toFixed(2)}`;

  for (let i = 0; i < pts.length - 1; i += 1) {
    const p0 = pts[i];
    const p1 = pts[i + 1];
    if (!p0 || !p1) continue;
    const dx = (p1.x - p0.x) / 3;
    d +=
      ` C${(p0.x + dx).toFixed(2)} ${(p0.y + ((m[i] ?? 0) / 3)).toFixed(2)}` +
      ` ${(p1.x - dx).toFixed(2)} ${(p1.y - ((m[i + 1] ?? 0) / 3)).toFixed(2)}` +
      ` ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`;
  }
  return d;
}

/**
 * Муруй дээрх `x` цэгийн `y`. Хяналтын цэгүүд x-ээр гуравны нэгээр
 * жигд тавигддаг тул x(t) нь t-ээс ШУГАМАН — хоёртын хайлт хэрэггүй.
 */
export function monotoneY(pts: Pt[], x: number): number {
  if (pts.length === 0) return 0;
  if (pts.length === 1) return pts[0]?.y ?? 0;

  const m = tangents(pts.map((p) => p.y));
  let i = pts.findIndex((_, k) => k < pts.length - 1 && x <= (pts[k + 1]?.x ?? 0));
  if (i === -1) i = pts.length - 2;

  const p0 = pts[i]!;
  const p1 = pts[i + 1]!;
  const t = p1.x === p0.x ? 0 : Math.min(1, Math.max(0, (x - p0.x) / (p1.x - p0.x)));
  const c1 = p0.y + (m[i] ?? 0) / 3;
  const c2 = p1.y - (m[i + 1] ?? 0) / 3;
  const u = 1 - t;
  return u * u * u * p0.y + 3 * u * u * t * c1 + 3 * u * t * t * c2 + t * t * t * p1.y;
}
